import { FieldOption, OperatorOption } from '../constants';

/**
 * Filter fields that use the chip-based multi-value picker. Each chip can be an
 * exact identifier or a wildcard pattern; multiple chips are OR-ed when building
 * the GraphQL query.
 */
const IDENT_CHIP_QUICK_SEARCH_FIELDS = new Set<string>([
  FieldOption.searchTerm.value,
  FieldOption.dam.value,
  FieldOption.sire.value,
  FieldOption.breeder.value,
  FieldOption.currentOwner.value,
  FieldOption.member.value,
]);

export function isAnimalIdentQuickSearchField(field: string): boolean {
  return (
    field === FieldOption.searchTerm.value ||
    field === FieldOption.dam.value ||
    field === FieldOption.sire.value
  );
}

export function isMemberIdentQuickSearchField(field: string): boolean {
  return (
    field === FieldOption.breeder.value ||
    field === FieldOption.currentOwner.value ||
    field === FieldOption.member.value
  );
}

export function isIdentChipQuickSearchField(field: string): boolean {
  return IDENT_CHIP_QUICK_SEARCH_FIELDS.has(field);
}

export type AnimalIdentChipExpansion = {
  operator: string;
  value: string;
};

/** Replaces `*` with `%` so legacy i4-style patterns work in the UI. */
export function normalizeWildcardInput(input: string): string {
  return input.replace(/\*/g, '%');
}

export function hasWildcardChars(value: string): boolean {
  return /[%*]/.test(value);
}

/** True when `%` appears only at the start and/or end (not in the middle). */
export function isEdgeOnlyWildcardPattern(pattern: string): boolean {
  const normalized = normalizeWildcardInput(pattern).trim();
  if (!normalized.includes('%')) return false;
  const inner = normalized.replace(/^%+|%+$/g, '');
  return !inner.includes('%');
}

/**
 * Derives comparison operators from a single chip's wildcard pattern.
 * Edge-only patterns map to one operator; mid-wildcard patterns expand via
 * {@link expandWildcardPattern}.
 */
export function expandAnimalIdentChip(chip: string): AnimalIdentChipExpansion {
  const trimmed = normalizeWildcardInput(chip).trim();
  if (!trimmed) return { operator: OperatorOption.equals.value, value: '' };

  if (!trimmed.includes('%')) {
    return { operator: OperatorOption.equals.value, value: trimmed };
  }

  const leading = trimmed.startsWith('%');
  const trailing = trimmed.endsWith('%');
  const base = trimmed.replace(/^%+|%+$/g, '').trim();

  if (leading && trailing) {
    return { operator: OperatorOption.contains.value, value: base };
  }
  if (leading) {
    return { operator: OperatorOption.endsWith.value, value: base };
  }
  if (trailing) {
    return { operator: OperatorOption.startsWith.value, value: base };
  }
  return { operator: OperatorOption.equals.value, value: trimmed };
}

/**
 * Reformats a SQL-LIKE pattern into one or more supported comparison
 * constraints. Mid-wildcard patterns (e.g. `AB%EFG`) become ANDed
 * STARTS_WITH / CONTAINS / ENDS_WITH rows on each branch field.
 */
export function expandWildcardPattern(
  pattern: string,
): AnimalIdentChipExpansion[] {
  const raw = normalizeWildcardInput(pattern).trim();
  if (!raw) return [];
  if (!raw.includes('%')) {
    return [{ operator: OperatorOption.equals.value, value: raw }];
  }

  if (isEdgeOnlyWildcardPattern(raw)) {
    const single = expandAnimalIdentChip(raw);
    return single.value ? [single] : [];
  }

  const segments = raw.split('%');
  const constraints: AnimalIdentChipExpansion[] = [];

  if (!raw.startsWith('%') && segments[0]) {
    constraints.push({
      operator: OperatorOption.startsWith.value,
      value: segments[0],
    });
  }

  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];
    if (!seg) continue;
    const isLast = i === segments.length - 1;
    if (isLast && !raw.endsWith('%')) {
      constraints.push({
        operator: OperatorOption.endsWith.value,
        value: seg,
      });
    } else {
      constraints.push({
        operator: OperatorOption.contains.value,
        value: seg,
      });
    }
  }

  return constraints;
}

/**
 * Splits a comma-separated chip-style value into per-chip expansions. Empty
 * chips are skipped. Mid-wildcard chips are left as a single expansion with the
 * raw pattern for {@link identChipGql} to branch.
 */
export function expandAnimalIdentChips(
  value: string,
): AnimalIdentChipExpansion[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .flatMap((chip) => {
      const normalized = normalizeWildcardInput(chip).trim();
      if (!normalized.includes('%') || isEdgeOnlyWildcardPattern(normalized)) {
        const exp = expandAnimalIdentChip(chip);
        return exp.value.length > 0 ? [exp] : [];
      }
      return [{ operator: '', value: normalized }];
    });
}

export function isAnimalIdentChipStyleFilter(
  operator: string,
  value: string,
): boolean {
  return isIdentChipStyleFilter(operator, value);
}

export function isIdentChipStyleFilter(
  operator: string,
  value: string,
): boolean {
  if (!value) return false;
  if (value.includes(',') || hasWildcardChars(value)) return true;
  return !operator;
}

export function legacyFilterToChipValue(
  operator: string,
  value: string,
): string {
  if (!value) return '';
  if (value.includes(',') || hasWildcardChars(value)) return value;
  switch (operator) {
    case OperatorOption.startsWith.value:
      return `${value}%`;
    case OperatorOption.endsWith.value:
      return `%${value}`;
    case OperatorOption.contains.value:
      return `%${value}%`;
    default:
      return value;
  }
}

/** Wraps plain text for mating-predictor ResolveByIdents (LIKE semantics). */
export function plainTextToLikePattern(term: string): string {
  const t = term.trim();
  if (!t) return '';
  if (hasWildcardChars(t)) return normalizeWildcardInput(t);
  return `%${t}%`;
}
